import { useEffect, useState } from 'react';
import { BaseCustomerChatbotConfigurationStyle } from '@endion/types';
import { ChatBubbleLeftRightIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CreateEndionChatbotOptions } from '../endion-chatbot';
import { useInitialMessage } from '../hooks/use-initial-message';
import { TRIGGER_CLASSNAME } from '../hooks/use-set-style.hook';
import { getHasClosedInitialMessage, setHasClosedInitialMessage } from '../utils/session-storage';

type BotTriggerButtonProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  style: BaseCustomerChatbotConfigurationStyle;
  replaceDefaultClickBehaviorWithLink?: CreateEndionChatbotOptions['replaceDefaultClickBehaviorWithLink'];
};

export function BotTriggerButton({ isOpen, setIsOpen, replaceDefaultClickBehaviorWithLink }: BotTriggerButtonProps) {
  const [firstMessageOpen, setFirstMessageOpen] = useState(!getHasClosedInitialMessage());
  const { initialMessage } = useInitialMessage();

  const handleOpenRequest = () => {
    if (replaceDefaultClickBehaviorWithLink?.href) return;
    setIsOpen(!isOpen);
  };

  // Close the initial message after 10 seconds
  useEffect(() => {
    if (firstMessageOpen) {
      const timer = setTimeout(() => {
        setFirstMessageOpen(false);
        setHasClosedInitialMessage();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [firstMessageOpen]);

  return (
    <div className={`fixed flex flex-col items-end ${TRIGGER_CLASSNAME}`}>
      {/* TODO: We might want to remove this feature (or enhance it) */}
      {/* Mask everything with an anchor. This feature is used to launch a Whatsapp chat from this trigger upon click. (Meta Demo requirement) */}
      {replaceDefaultClickBehaviorWithLink?.href ? (
        <a href={replaceDefaultClickBehaviorWithLink?.href} target="_blank" className="absolute block w-full h-full" />
      ) : null}
      {firstMessageOpen && initialMessage ? (
        <>
          <div className="bg-gray-400 rounded-full mb-2 cursor-pointer opacity-80 hover:opacity-70 transition-opacity">
            <XMarkIcon
              height={20}
              className=" text-white "
              onClick={() => {
                setFirstMessageOpen(false);
                setHasClosedInitialMessage();
              }}
            />
          </div>
          <div
            className="shadow-[0px_0px_16px_-4px_rgba(0,0,0,0.2)] bg-white 
      py-4 px-5 rounded-md text-base max-w-[240px] cursor-pointer mb-4"
            onClick={handleOpenRequest}
          >
            <p className="line-clamp-4 leading-[22px]">{initialMessage}</p>
          </div>
        </>
      ) : null}
      <button
        onClick={handleOpenRequest}
        className="group rounded-full h-[60px] w-[60px] bg-primary flex items-center justify-center hover:opacity-85 transition-opacity duration-150"
      >
        {!isOpen ? (
          <ChatBubbleLeftRightIcon height={28} className="text-white" />
        ) : (
          <ChevronDownIcon height={38} className="text-white" />
        )}
      </button>
    </div>
  );
}
